import Vue from 'vue';
import VueRouter, {RouteConfig} from 'vue-router';
import AppMain from '@/views/AppMain.vue';
import Login from '@/views/login/Login.vue';
import AppSession from '@/store/AppSession';
import UserAuthenticationApi from '@/api/UserAuthentication';
import {CommonModelUtils, UserAuthResponse} from '@/model/CommonModel';
import store from '@/store/index';
import CommonUtils from '@/utils/CommonUtils';
import ApiUtils from '@/utils/ApiUtils';
import {i18n} from '@/plugins/VueI18n';
import MessageUtils from '@/utils/MessageUtils';
import SystemAdminApi from '@/api/SystemAdminApi';
import {trackerUserLogin, trackEventRaygun} from '@/plugins/raygun';
import VueCookies from 'vue-cookies';
import {Location, RawLocation} from 'vue-router/types/router';

Vue.use(VueRouter);

/**
 * 각 메뉴 별 Root component는 메뉴 이름을 따른다.
 * ex) CASE INFO -> CaseInfo
 * Monotoring -> Monitoring
 *
 * 모든 Pagination이 처리 되는 컴포넌트는 List라는 suppfix를 붙인다.
 * ex) CaseInfoSenderList, CaseList ...
 *
 * 모든 Pagination이 처리 도는 컴포넌트는 /:pageNo?/:rowCount?/:hash? URI를 path의 suffix로 가진다
 *
 * List, Detail이 나뉘는 컴포넌트의 경우 List의 path는 /list, Detail의 path는 :key/detail 형태로 구분하는 것을 권고한다.
 *
 * meta.menuId: 해당 View에서 사용 할 메뉴 아이디를 기재한다.
 * Pagination이 처리되는 Component에선 메뉴 아이디를 기반으로 컬럼을 조회한다.
 *
 * meta.depth1: 해당 View 기준 Active 처리 될 SideMenu의 router name을 기재한다
 * meta.depth2: 해당 View 기준 Active 처리 될 2 depth (Tab 등) 메뉴의 router name을 기재한다
 */
const routes: RouteConfig[] = [
    {
        path: '/password/change/:token/:language?',
        name: 'AlibabaSetPassword',
        meta: {needAuthentication: false},
        component: () => import('../views/login/AlibabaSetPassword.vue')
    },
    {
        path: '/password/finish',
        name: 'AlibabaSetPasswordFinish',
        meta: {needAuthentication: false},
        component: () => import('../views/login/AlibabaSetPasswordFinish.vue')
    },
    {
        path: '/login/:language?/:sponsorKey?/:sessionSenderKey?/:appendix?',
        name: 'Login',
        meta: {needAuthentication: false},
        component: () => import(/* webpackChunkName: "Home" */'../views/login/Login.vue')
    },
    {
        path: '/exam',
        name: 'Exam',
        meta: {needAuthentication: false},
        component: () => import('../views/0exam/Exam.vue')
    },
    {
        path: '/exam/form',
        name: 'ExamForm',
        meta: {needAuthentication: false},
        component: () => import('../views/0exam/ExamForm.vue')
    },
    {
        path: '/whodd',
        name: 'WhoddForm',
        meta: {needAuthentication: false},
        component: () => import('../views/0exam/WhoddForm.vue')
    },
    {
        path: '/exam-editor',
        name: 'TiptapEditor',
        meta: {needAuthentication: false},
        component: () => import('../views/0exam/ExamTipTapEditor.vue')
    },
    {
        path: '/find-password/:language?',
        name: 'FindPassword',
        meta: {needAuthentication: false},
        component: () => import('../views/login/FindPassword.vue')
    },
    {
        path: '/',
        name: 'AppMain',
        component: AppMain,
        redirect: '/home',
        children: [
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/bad-permission',
                name: 'Forbidden',
                meta: {},
                component: () => import(/* webpackChunkName: "Home" */'../views/Forbidden.vue')
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/home',
                name: 'Home',
                meta: {menuId: 'MU-HOME', depth1: 'Home'},
                component: () => import(/* webpackChunkName: "Home" */'../views/home/Home.vue')
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/home/notice',
                name: 'Notice',
                meta: {menuId: 'MU-HM-NT', depth1: 'Home', depth2: 'Notice'},
                component: () => import(/* webpackChunkName: "Notice1" */'../views/home/Notice.vue'),
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/home/notice/list/:pageNo?/:rowCount?/:hash?',
                        name: 'NoticeList',
                        meta: {
                            menuId: 'MU-HM-NT', depth1: 'Home', depth2: 'NoticeList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Notice1" */'@/views/home/NoticeList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/home/notice/:noticeKey/detail',
                        name: 'NoticeDetail',
                        meta: {menuId: 'MU-HM-NT', depth1: 'Home', depth2: 'NoticeList'},
                        component: () => import(/* webpackChunkName: "Notice1" */'../views/home/NoticeDetail.vue')
                    },
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/home/report',
                name: 'CaseReportDeadlineIsApproaching',
                meta: {privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-HM-RP', depth1: 'Home', depth2: 'CaseReportDeadlineIsApproaching'},
                component: () => import(/* webpackChunkName: "Notice2" */'../views/home/CaseReportDeadlineIsApproaching.vue'),
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/home/report/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseReportDeadlineIsApproachingList',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-HM-RP', depth1: 'Home',
                            depth2: 'CaseReportDeadlineIsApproachingList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Notice2" */'../views/home/CaseReportDeadlineIsApproachingList.vue')
                    },
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/home/edcsync',
                name: 'EdcSync',
                meta: {privId: ['READ.SYNC', 'EDIT.SYNC'], menuId: 'MU-HM-ES-LT', depth1: 'Home', depth2: 'edcSync'},
                component: () => import(/* webpackChunkName: "Notice2" */'../views/home/EdcSync.vue'),
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/home/edcsync/list/:pageNo?/:rowCount?/:hash?',
                        name: 'EdcSyncList',
                        meta: {
                            privId: ['READ.SYNC', 'EDIT.SYNC'], menuId: 'MU-HM-ES-LT', depth1: 'Home',
                            depth2: 'EdcSyncList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Notice2" */'../views/home/EdcSyncList.vue')
                    },
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case',
                name: 'Case',
                meta: {privId: ['READ.CASE', 'EDIT.CASE'], depth1: 'Case', menuId: 'MU-CS'},
                component: () => import(/* webpackChunkName: "Case" */'../views/case/Case.vue'),
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseList',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS-LIST', depth1: 'Case', depth2: 'CaseList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Case" */'../views/case/CaseList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/warning/:target?/:key?/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseWarningList',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS-WARNING', depth1: 'Case', depth2: 'CaseWarningList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Case" */'../views/case/CaseWarningList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/email-log/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseEmailLogList',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS-LOG', depth1: 'Case', depth2: 'CaseEmailLogList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Case" */'../views/case/CaseEmailLogList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/duplication-check',
                        name: 'AddCase',
                        meta: {privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', depth1: 'Case', depth2: 'CaseList'},
                        component: () => import(/* webpackChunkName: "Case" */'../views/case/CaseDuplicationCheck.vue')
                    },
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail',
                name: 'CaseDetail',
                component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CaseDetail.vue'),
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/CI/:hash?',
                        name: 'CaseCI',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'CI', nextRouteName: 'CaseRP', depth1: 'Case',
                            depth2: 'CaseCI'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CaseIdentification.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/RP/summary/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseRP',
                        meta: {privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'RP', depth1: 'Case', depth2: 'CaseRP'},
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CasePrimarySourceSummary.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/RP/:rowNo/:hash?',
                        name: 'CaseRPDetail',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'RP', nextRouteName: 'CaseSD', depth1: 'Case',
                            depth2: 'CaseRP'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CasePrimarySource.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/SD/:hash?',
                        name: 'CaseSD',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'SD', nextRouteName: 'CaseLR', depth1: 'Case',
                            depth2: 'CaseSD'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail4" */'../views/case/CaseSender.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/LR/summary/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseLR',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'LR', nextRouteName: 'CaseSI', depth1: 'Case',
                            depth2: 'CaseLR'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail4" */'../views/case/CaseLiteratureSummary.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/LR/:rowNo/:hash?',
                        name: 'CaseLRDetail',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'LR', nextRouteName: 'CaseSI', depth1: 'Case',
                            depth2: 'CaseLR'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail4" */'../views/case/CaseLiterature.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/SI/:hash?',
                        name: 'CaseSI',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'SI', nextRouteName: 'CaseDM', depth1: 'Case',
                            depth2: 'CaseSI'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CaseStudy.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/DM/:hash?',
                        name: 'CaseDM',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'DM', nextRouteName: 'CaseDH', depth1: 'Case',
                            depth2: 'CaseDM'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CaseDemo.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/DH/summary/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseDH',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'DH', nextRouteName: 'CaseAE', depth1: 'Case',
                            depth2: 'CaseDH'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CaseDrugHistorySummary.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/DH/:rowNo/:hash?',
                        name: 'CaseDHDetail',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'DH', nextRouteName: 'CaseAE', depth1: 'Case',
                            depth2: 'CaseDH'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CaseDrugHistory.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/AE/summary/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseAE',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'AE', nextRouteName: 'CaseLB', depth1: 'Case',
                            depth2: 'CaseAE'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail2" */'../views/case/CaseAdverseEventSummary.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/AE/:rowNo/:hash?',
                        name: 'CaseAEDetail',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'AE', nextRouteName: 'CaseLB', depth1: 'Case',
                            depth2: 'CaseAE'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail2" */'../views/case/CaseAdverseEvent.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/LB/summary/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseLB',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'LB', nextRouteName: 'CaseDG', depth1: 'Case',
                            depth2: 'CaseLB'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CaseLabSummary.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/LB/:rowNo/:hash?',
                        name: 'CaseLBDetail',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'LB', nextRouteName: 'CaseDG', depth1: 'Case',
                            depth2: 'CaseLB'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CaseLab.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/DG/summary/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseDG',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'DG', nextRouteName: 'CaseNR', depth1: 'Case',
                            depth2: 'CaseDG'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CaseDrugSummary.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/DG/:rowNo/:hash?',
                        name: 'CaseDGDetail',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'DG', nextRouteName: 'CaseNR', depth1: 'Case',
                            depth2: 'CaseDG'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail1" */'../views/case/CaseDrug.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/NR/:hash?',
                        name: 'CaseNR',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CS', pageId: 'NR', nextRouteName: 'CaseRE', depth1: 'Case',
                            depth2: 'CaseNR'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail3" */'../views/case/CaseNarrative.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/RE/:hash?',
                        name: 'CaseRE',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'],
                            menuId: 'MU-CS-DT-TAB-RE',
                            pageId: 'RE',
                            depth1: 'Case',
                            depth2: 'CaseRE'
                        },
                        component: () => import(/* webpackChunkName: "CaseDetail4" */'../views/case/CaseReceiver.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/WF/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseWF',
                        meta: {
                            privId: ['READ.CASE_WORKFLOW'],
                            menuId: 'MU-CS-DT-TAB-WF',
                            depth1: 'Case',
                            depth2: 'CaseWF'
                        },
                        component: () => import('../views/case/CaseWorkflow.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/case/:caseKey/detail/AT/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseAT',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'],
                            menuId: 'MU-CS-DT-TAB-AT',
                            depth1: 'Case',
                            depth2: 'CaseAT'
                        },
                        component: () => import('../views/case/CaseAT.vue')
                    },
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/coding',
                name: 'Coding',
                component: () => import('../views/coding/Coding.vue'),
                meta: {privId: ['READ.CASE', 'EDIT.CASE'], depth1: 'Coding', menuId: 'MU-CD'},
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/coding/product/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CodingProductList',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CD-PRD',
                            depth1: 'Coding', depth2: 'CodingProductList', viewType: 'ListView'
                        },
                        component: () => import('@/views/coding/CodingProductList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/coding/mfds/product/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CodingMfdsProductList',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CD-MFDS-PRD',
                            depth1: 'Coding', depth2: 'CodingMfdsProductList', viewType: 'ListView'
                        },
                        component: () => import('@/views/coding/CodingMfdsProductList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/coding/substance/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CodingSubstanceList',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CD-SUBS',
                            depth1: 'Coding', depth2: 'CodingSubstanceList', viewType: 'ListView'
                        },
                        component: () => import('@/views/coding/CodingWhoddSubstanceList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/coding/mfds/substance/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CodingMfdsSubstanceList',
                        meta: {
                            privId: ['READ.CASE', 'EDIT.CASE'], menuId: 'MU-CD-MFDS-SUBS',
                            depth1: 'Coding', depth2: 'CodingMfdsSubstanceList', viewType: 'ListView'
                        },
                        component: () => import('@/views/coding/CodingMfdsSubstanceList.vue')
                    },
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo',
                name: 'CaseInfo',
                component: () => import(/* webpackChunkName: "CaseInfo1" */'@/views/case-info/CaseInfo.vue'),
                meta: {privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI', depth1: 'CaseInfo'},
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/sender/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseInfoSenderList',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-SD', depth1: 'CaseInfo',
                            depth2: 'CaseInfoSenderList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "CaseInfo1" */'@/views/case-info/CaseInfoSenderList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/sender/:senderKey/detail',
                        name: 'CaseInfoSenderDetail',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-SD', depth1: 'CaseInfo',
                            depth2: 'CaseInfoSenderList'
                        },
                        component: () =>
                            import(/* webpackChunkName: "CaseInfo1" */'@/views/case-info/CaseInfoSenderDetail.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/receiver/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseInfoReceiverList',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-MF', depth1: 'CaseInfo',
                            depth2: 'CaseInfoReceiverList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "CaseInfo1" */'@/views/case-info/CaseInfoReceiverList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/receiver/:receiverKey/detail',
                        name: 'CaseInfoReceiverDetail',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-MF', depth1: 'CaseInfo',
                            depth2: 'CaseInfoReceiverList'
                        },
                        component: () =>
                            import(/* webpackChunkName: "CaseInfo1" */'@/views/case-info/CaseInfoReceiverDetail.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/product/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseInfoProductList',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-PD', depth1: 'CaseInfo',
                            depth2: 'CaseInfoProductList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "CaseInfo2" */'@/views/case-info/CaseInfoProductList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/product/:productKey/detail',
                        name: 'CaseInfoProductDetail',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-PD', depth1: 'CaseInfo',
                            depth2: 'CaseInfoProductList'
                        },
                        component: () =>
                            import(/* webpackChunkName: "CaseInfo2" */'@/views/case-info/CaseInfoProductDetail.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/reporter/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseInfoReporterList',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-SR', depth1: 'CaseInfo',
                            depth2: 'CaseInfoReporterList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "CaseInfo2" */'@/views/case-info/CaseInfoReporterList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/reporter/:reporterKey/detail',
                        name: 'CaseInfoReporterDetail',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-SR', depth1: 'CaseInfo',
                            depth2: 'CaseInfoReporterList'
                        },
                        component: () =>
                            import(/* webpackChunkName: "CaseInfo2" */'@/views/case-info/CaseInfoReporterDetail.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/study/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseInfoStudyList',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-ST', depth1: 'CaseInfo',
                            depth2: 'CaseInfoStudyList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "CaseInfo2" */'@/views/case-info/CaseInfoStudyList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/study/:studyKey/detail',
                        name: 'CaseInfoStudyDetail',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-ST', depth1: 'CaseInfo',
                            depth2: 'CaseInfoStudyList'
                        },
                        component: () =>
                            import(/* webpackChunkName: "CaseInfo2" */'@/views/case-info/CaseInfoStudyDetail.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/narrative/list/:pageNo?/:rowCount?/:hash?',
                        name: 'CaseInfoNarrativeList',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-NR', depth1: 'CaseInfo',
                            depth2: 'CaseInfoNarrativeList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "CaseInfo2" */'@/views/case-info/CaseInfoNarrativeList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/caseinfo/narrative/:nrKey/detail',
                        name: 'CaseInfoNarrativeDetail',
                        meta: {
                            privId: ['READ.CASE_INFO', 'EDIT.CASE_INFO'], menuId: 'MU-CI-NR', depth1: 'CaseInfo',
                            depth2: 'CaseInfoNarrativeList'
                        },
                        component: () =>
                            import(/* webpackChunkName: "CaseInfo2" */'@/views/case-info/CaseInfoNarrativeDetail.vue')
                    }
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/data',
                name: 'Data',
                component: () => import(/* webpackChunkName: "Data1" */'@/views/data/Data.vue'),
                redirect: '/:sponsorKey/:sessionSenderKey/:language/:appendix/data/export',
                meta: {privId: ['READ.DATA', 'EDIT.DATA'], menuId: 'MU-DT', depth1: 'Data'},
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/data/export',
                        name: 'DataExport',
                        meta: {
                            privId: 'EDIT.DATA', forbiddenRedirect: 'DataHistoryList', menuId: 'MU-DT-EX-CS', depth1: 'Data',
                            depth2: 'DataExport'
                        },
                        component: () => import(/* webpackChunkName: "Data1" */'@/views/data/DataExport.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/data/select-domain',
                        name: 'DataSetSelectDomain',
                        meta: {
                            privId: 'EDIT.DATA', forbiddenRedirect: 'DataHistoryList', menuId: 'MU-DT-LB-EX', depth1: 'Data',
                            depth2: 'DataExport'
                        },
                        component: () => import(/* webpackChunkName: "Data2" */'@/views/data/DataSetSelectDomain.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/data/import',
                        name: 'DataImport',
                        meta: {
                            privId: 'EDIT.DATA', forbiddenRedirect: 'DataHistoryList', menuId: 'MU-DT-IM', depth1: 'Data',
                            depth2: 'DataImport'
                        },
                        component: () => import(/* webpackChunkName: "Data2" */'@/views/data/DataImport.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/data/history/list/:pageNo?/:rowCount?/:hash?',
                        name: 'DataHistoryList',
                        meta: {
                            privId: ['READ.DATA', 'EDIT.DATA'], menuId: 'MU-DT-HS', depth1: 'Data', depth2: 'DataHistoryList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Data2" */'@/views/data/DataHistoryList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/data/history/:dataKey/import-detail/:pageNo?/:rowCount?/:hash?',
                        name: 'DataHistoryImportDetailList',
                        meta: {
                            privId: ['READ.DATA', 'EDIT.DATA'], menuId: 'MU-DT-HS-DT', depth1: 'Data', depth2: 'DataHistoryList',
                            viewType: 'ListView'
                        },
                        component: () => import('@/views/data/DataHistoryImportDetailList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/data/library/list/:pageNo?/:rowCount?/:hash?',
                        name: 'DataLibraryList',
                        meta: {
                            privId: 'EDIT.DATA', forbiddenRedirect: 'DataHistoryList', menuId: 'MU-DT-LB', depth1: 'Data',
                            depth2: 'DataLibraryList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Data2" */'@/views/data/DataLibraryList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/data/library/:libraryKey/detail',
                        name: 'DataLibraryDetail',
                        meta: {
                            privId: 'EDIT.DATA', forbiddenRedirect: 'DataHistoryList', menuId: 'MU-DT-LB', depth1: 'Data',
                            depth2: 'DataLibraryList'
                        },
                        component: () => import(/* webpackChunkName: "Data2" */'@/views/data/DataLibraryDetail.vue')
                    },
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/monitoring',
                name: 'Monitoring',
                meta: {privId: ['READ.MONITORING', 'EDIT.MONITORING'], menuId: 'MU-MO', depth1: 'Monitoring'},
                component: () => import(/* webpackChunkName: "Monitoring1" */'@/views/monitoring/Monitoring.vue'),
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/monitoring/monitoring',
                        name: 'MonitoringDetail',
                        meta: {
                            privId: 'EDIT.MONITORING', forbiddenRedirect: 'MonitoringHistoryList', menuId: 'MU-MO-EX', depth1: 'Monitoring',
                            depth2: 'MonitoringDetail'
                        },
                        component: () => import(/* webpackChunkName: "Monitoring1" */'@/views/monitoring/MonitoringDetail.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/monitoring/history/list/:pageNo?/:rowCount?/:hash?',
                        name: 'MonitoringHistoryList',
                        meta: {
                            privId: ['READ.MONITORING', 'EDIT.MONITORING'], menuId: 'MU-MO-HS', depth1: 'Monitoring',
                            depth2: 'MonitoringHistoryList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Monitoring1" */'@/views/monitoring/MonitoringHistoryList.vue')
                    }
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/submission',
                name: 'Submission',
                meta: {privId: ['READ.SUBMISSION', 'EDIT.SUBMISSION'], menuId: 'MU-SM', depth1: 'Submission'},
                component: () => import(/* webpackChunkName: "Submission1" */'@/views/submission/Submission.vue'),
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/submission/submission/:caseKey?',
                        name: 'SubmissionDetail',
                        meta: {
                            privId: 'EDIT.SUBMISSION', forbiddenRedirect: 'SubmissionHistoryList', menuId: 'MU-SM', depth1: 'Submission',
                            depth2: 'SubmissionDetail'
                        },
                        component: () => import(/* webpackChunkName: "Submission1" */'@/views/submission/SubmissionDetail.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/submission/:batchKey/check-list/:notation/:pageNo?/:rowCount?/:hash?',
                        name: 'SubmissionCheckList',
                        meta: {
                            privId: 'EDIT.SUBMISSION', forbiddenRedirect: 'SubmissionHistoryList', menuId: 'MU-SM-CK', depth1: 'Submission',
                            depth2: 'SubmissionCheckList'
                        },
                        component: () => import(/* webpackChunkName: "Submission1" */'@/views/submission/SubmissionCheckList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/submission/history/list/:pageNo?/:rowCount?/:hash?',
                        name: 'SubmissionHistoryList',
                        meta: {
                            privId: ['READ.SUBMISSION', 'EDIT.SUBMISSION'], menuId: 'MU-SM-HS', depth1: 'Submission',
                            depth2: 'SubmissionHistoryList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Submission2" */'@/views/submission/SubmissionHistoryList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/submission/history/:batchKey/batch-detail/:pageNo?/:rowCount?/:hash?',
                        name: 'SubmissionHistoryDetailList',
                        meta: {
                            privId: ['READ.SUBMISSION', 'EDIT.SUBMISSION'], menuId: 'MU-SM-HS-DT', depth1: 'Submission',
                            depth2: 'SubmissionHistoryList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Submission2" */'@/views/submission/SubmissionHistoryDetailList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/submission/case-history/list/:pageNo?/:rowCount?/:hash?',
                        name: 'SubmissionCaseHistoryList',
                        meta: {
                            privId: ['READ.SUBMISSION', 'EDIT.SUBMISSION'], menuId: 'MU-SM-CH', depth1: 'Submission',
                            depth2: 'SubmissionCaseHistoryList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Submission2" */'@/views/submission/SubmissionCaseHistoryList.vue')
                    }
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/sync',
                name: 'Sync',
                meta: {privId: ['READ.SYNC', 'EDIT.SYNC', 'READ.MAPPING', 'EDIT.MAPPING'], menuId: 'MU-SYNC', depth1: 'Sync'},
                component: () => import(/* webpackChunkName: "Sync1" */'@/views/sync/Sync.vue'),
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/sync/study-list/:pageNo?/:rowCount?/:hash?',
                        name: 'SyncStudyList',
                        meta: {
                            privId: ['READ.SYNC', 'EDIT.SYNC', 'READ.MAPPING', 'EDIT.MAPPING'], menuId: 'MU-ES', depth1: 'Sync',
                            depth2: 'SyncStudyList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Sync1" */'@/views/sync/SyncStudyList.vue')
                    }
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/sync/study-detail',
                name: 'SyncStudyDetail',
                meta: {privId: ['READ.SYNC', 'EDIT.SYNC', 'READ.MAPPING', 'EDIT.MAPPING'], menuId: 'MU-SYNC', depth1: 'Sync'},
                component: () => import(/* webpackChunkName: "Sync2" */'@/views/sync/SyncStudyDetail.vue'),
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/sync/study-list/:studyKey/detail/mapping-rule/list/:pageNo?/:rowCount?/:hash?',
                        name: 'StudyMappingRuleList',
                        meta: {
                            privId: ['READ.SYNC', 'EDIT.SYNC', 'READ.MAPPING', 'EDIT.MAPPING'], menuId: 'MU-ES-IL', depth1: 'Sync',
                            depth2: 'StudyMappingRuleList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Sync2" */'@/views/sync/StudyMappingRuleList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/sync/study-list/:studyKey/detail/mapping-rule/summary/list/:itemId',
                        name: 'MappingRuleSummaryList',
                        meta: {
                            privId: ['READ.MAPPING', 'EDIT.MAPPING'], menuId: 'MU-ES-ID', depth1: 'Sync', depth2: 'StudyMappingRuleList'
                        },
                        component: () => import(/* webpackChunkName: "Sync2" */'@/views/sync/MappingRuleSummaryList.vue'),
                        children: [
                            {
                                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/sync/study-list/:studyKey/mapping-rule/:itemId/:targetSeqNo/detail',
                                name: 'StudyMappingRuleDetail',
                                meta: {
                                    privId: ['READ.MAPPING', 'EDIT.MAPPING'], menuId: 'MU-ES-ID', depth1: 'Sync',
                                    depth2: 'StudyMappingRuleList'
                                },
                                component: () => import(/* webpackChunkName: "Sync2" */'@/views/sync/StudyMappingRuleDetail.vue')
                            }
                        ]
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/sync/study-list/:studyKey/detail/sync/list/:pageNo?/:rowCount?/:hash?',
                        name: 'SyncList',
                        meta: {
                            privId: ['READ.SYNC', 'EDIT.SYNC'], menuId: 'MU-HM-ES', depth1: 'Sync', depth2: 'SyncList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Sync2" */'@/views/sync/SyncList.vue')
                    }
                ]
            },
            {
                path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/admin',
                name: 'Admin',
                component: () => import(/* webpackChunkName: "Admin1" */'@/views/admin/Admin.vue'),
                meta: {privId: ['READ.ADMIN', 'EDIT.ADMIN'], menuId: 'MU-AD', depth1: 'Admin'},
                children: [
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/admin/user/list/:pageNo?/:rowCount?/:hash?',
                        name: 'AdminUserList',
                        meta: {
                            privId: ['READ.ADMIN', 'EDIT.ADMIN'], menuId: 'MU-AD-US', depth1: 'Admin', depth2: 'AdminUserList',
                            viewType: 'ListView'
                        },
                        component: () => import(/* webpackChunkName: "Admin1" */'@/views/admin/AdminUserList.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/admin/user/:userKey/detail',
                        name: 'AdminUserDetail',
                        meta: {privId: ['READ.ADMIN', 'EDIT.ADMIN'], menuId: 'MU-AD-US', depth1: 'Admin', depth2: 'AdminUserList'},
                        component: () => import(/* webpackChunkName: "Admin1" */'@/views/admin/AdminUserDetail.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/admin/role',
                        name: 'AdminRole',
                        meta: {privId: ['READ.ADMIN', 'EDIT.ADMIN'], menuId: 'MU-AD-RL', depth1: 'Admin', depth2: 'AdminRole'},
                        component: () => import(/* webpackChunkName: "Admin2" */'@/views/admin/AdminRole.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/admin/role-priv',
                        name: 'AdminRolePrivilege',
                        meta: {privId: ['READ.ADMIN', 'EDIT.ADMIN'], menuId: 'MU-AD-PR', depth1: 'Admin', depth2: 'AdminRolePrivilege'},
                        component: () => import(/* webpackChunkName: "Admin2" */'@/views/admin/AdminRolePrivilege.vue')
                    },
                    {
                        path: '/:sponsorKey/:sessionSenderKey/:language/:appendix/admin/settings',
                        name: 'AdminSettings',
                        meta: {privId: ['READ.ADMIN', 'EDIT.ADMIN'], menuId: 'MU-AD-ST', depth1: 'Admin', depth2: 'AdminSettings'},
                        component: () => import(/* webpackChunkName: "Admin2" */'@/views/admin/AdminSettings.vue')
                    }
                ]
            },
            {
                path: '/system-admin',
                name: 'SystemAdmin',
                meta: { isAdmin : true },
                redirect: '/system-admin/notice',
                component: () => import('@/views/system-admin/SystemAdminMain.vue'),
                children: [
                    {
                        path: '/system-admin/notice',
                        name: 'AdminNotice',
                        meta: {isAdmin: true, menuId: 'AD-MU-NT', depth1: 'Notice'},
                        redirect: '/system-admin/notice/list',
                        component: () => import('@/views/home/Notice.vue'),
                        children: [
                            {
                                path: '/system-admin/notice/list/:pageNo?/:rowCount?/:hash?',
                                name: 'AdminNoticeList',
                                meta: {
                                    isAdmin: true, menuId: 'AD-MU-NT', depth1: 'Notice', depth2: 'NoticeList',
                                    viewType: 'ListView'
                                },
                                component: () => import('@/views/home/NoticeList.vue')
                            },
                            {
                                path: '/system-admin/notice/:noticeKey/detail',
                                name: 'AdminNoticeDetail',
                                meta: {isAdmin: true, menuId: 'AD-MU-NT', depth1: 'Notice', depth2: 'NoticeList'},
                                component: () => import('@/views/home/NoticeDetail.vue')
                            }
                        ]
                    },
                     {
                        path: '/system-admin/company',
                        name: 'AdminCompany',
                        meta: { isAdmin: true, menuId: 'AD-MU-CO', depth1: 'Company'},
                        component: () => import('@/views/system/System.vue'),
                        children: [
                            {
                                path: '/system-admin/company/list/:pageNo?/:rowCount?/:hash?',
                                name: 'AdminCompanyList',
                                meta: { isAdmin: true, menuId: 'AD-MU-CO', depth1: 'Company', depth2: 'AdminCompanyList', viewType: 'ListView'},
                                component: () => import('@/views/system/CompanyList.vue'),
                            }
                        ]
                    },
                    {
                        path: '/system-admin/company/:companyKey',
                        name: 'AdminCompanyDetail',
                        meta: {isAdmin: true, depth1: 'Company', depth2: 'CompanyProperty'},
                        component: () => import('@/views/system/CompanyDetail.vue'),
                        children: [
                            {
                                path: '/system-admin/company/:companyKey/property',
                                name: 'AdminCompanyProperty',
                                meta: {isAdmin: true, menuId: 'AD-MU-CO', depth1: 'Company', depth2: 'AdminCompanyProperty'},
                                component: () => import('@/views/system/CompanyProperty.vue')
                            },
                            {
                                path: '/system-admin/company/:companyKey/user/list/:pageNo?/:rowCount?/:hash?',
                                name: 'AdminCompanyUserList',
                                meta: {isAdmin: true, menuId: 'AD-MU-CO-US', depth1: 'Company', depth2: 'AdminCompanyUserList', viewType: 'ListView'},
                                component: () => import('@/views/system/UserList.vue')
                            },
                            {
                                path: '/system-admin/company/:companyKey/study-mapping/list/:pageNo?/:rowCount?/:hash?',
                                name: 'AdminStudyMappingList',
                                meta: {isAdmin: true, menuId: 'AD-MU-CO-ST', depth1: 'Company', depth2: 'AdminStudyMappingList', viewType: 'ListView'},
                                component: () => import('@/views/system/StudyMappingList.vue'),
                            },
                            {
                                path: '/system-admin/company/:companyKey/study-mapping/:studyKey/detail',
                                name: 'AdminStudyMappingDetail',
                                meta: {isAdmin: true, menuId: 'AD-MU-CO-ST', depth1: 'Company', depth2: 'AdminStudyMappingList'},
                                component: () => import('@/views/system/StudyMappingDetail.vue')
                            }
                        ]
                    },
                    {
                        path: '/system-admin/search',
                        name: 'AdminSearch',
                        redirect: '/system-admin/search/data',
                        meta: {isAdmin: true, menuId: 'AD-MU-SE', depth1: 'Search'},
                        component: () => import('@/views/system-admin/search/Search.vue'),
                        children: [
                            {
                                path: '/system-admin/search/data/:pageNo?/:rowCount?/:hash?',
                                name: 'AdminSearchData',
                                meta: { isAdmin: true, menuId: 'AD-MU-SE-DT', depth1: 'Search', depth2: 'AdminSearchData'},
                                component: () => import('@/views/system-admin/search/SearchData.vue'),
                            },
                            {
                                path: '/system-admin/search/submission/:pageNo?/:rowCount?/:hash?',
                                name: 'AdminSearchSubmission',
                                meta: { isAdmin: true, menuId: 'AD-MU-SE-SM', depth1: 'Search', depth2: 'AdminSearchSubmission'},
                                component: () => import('@/views/system-admin/search/SearchSubmission.vue'),
                            }
                        ]
                    },
                    {
                        path: '/system-admin/caseinfo',
                        name: 'AdminCaseInfo',
                        redirect: '/system-admin/caseinfo/receiver',
                        meta: {isAdmin: true, menuId: 'AD-MU-CI', depth1: 'CaseInfo'},
                        component: () => import('@/views/system-admin/case-info/SystemAdminCaseInfo.vue'),
                        children: [
                            {
                                path: '/system-admin/caseinfo/receiver',
                                name: 'SystemAdminReceiver',
                                meta: { isAdmin: true, menuId: 'AD-MU-CI-RE', depth1: 'CaseInfo', depth2: 'SystemAdminReceiver'},
                                component: () => import('@/views/system-admin/case-info/SystemAdminCaseInfoReceiver.vue'),
                            }
                        ]
                    },
                    {
                        path: '/system-admin/user',
                        name: 'AdminUser',
                        meta: {isAdmin: true, menuId: 'AD-MU-US', depth1: 'User'},
                        component: () => import('@/views/system-admin/user/SystemAdminUser.vue'),
                        children: [
                            {
                                path: '/system-admin/user/list/:pageNo?/:rowCount?/:hash?',
                                name: 'SystemAdminUserList',
                                meta: { isAdmin: true, menuId: 'AD-MU-US', depth1: 'User', depth2: 'SystemAdminUserList',
                                        viewType: 'ListView'},
                                component: () => import('@/views/admin/AdminUserList.vue')
                            },
                            {
                                path: '/system-admin/user/:userKey/detail',
                                name: 'SystemAdminUserDetail',
                                meta: { isAdmin: true, menuId: 'AD-MU-US', depth1: 'User', depth2: 'SystemAdminUserList'},
                                component: () => import('@/views/admin/AdminUserDetail.vue')
                            }
                        ]
                    }
                ]
            },
        ]
    }
];

const router = new VueRouter(
    {
        mode: 'history',
        base: process.env.BASE_URL,
        routes
    });

router.beforeEach((to, from, next) => {
    trackEventRaygun(to.path);
    MessageUtils.clear();

    const isAdmin = to.meta.isAdmin;
    const sponsorKey = isAdmin ? '1' : to.params.sponsorKey;
    const appendix = to.params.appendix;
    const language = isAdmin ? 'en' : to.params.language;
    const senderKey = to.params.sessionSenderKey;


    store.commit('setParam', {sponsorKey, appendix, language, senderKey});
    
    if (from.path === to.path) {
        return;
    }
    
    if (to.meta.needAuthentication === false || to.name === 'Forbidden') {
        toNext();
        return;
    }



    function toNext(location?: any) {
        const {get: getCookie, set: setCookie} = VueCookies as any;
        const nextLang = language ?? getCookie('language') ?? 'en';
        setCookie('language', nextLang, Infinity);
        if (from.params.language !== nextLang) {
            const html = document.documentElement; // returns the html tag
            html.setAttribute('lang', nextLang);
            i18n.locale = nextLang;
        }

        if (location) {
            next({
                ...location,
                params: {
                    ...location.params,
                    language: nextLang,
                }
            });
        } else {
            if (to && to.params.language !== nextLang) {
                next({
                    ...to as Location,
                    params: {
                        ...to.params,
                        language: nextLang,
                    }
                });
                return;
            }
            next();
        }
    }

    if (to.name === 'Login') {
        if (AppSession.isUserLoggedIn(sponsorKey)) {
            next('/');
            return;
        }
        toNext();
        return;
    }
    
    if (!AppSession.isUserLoggedIn(sponsorKey)) {
        const extractRequiredParams = ApiUtils.extractRequiredParams(to.params);
        if (to.path === '/home') {
            toNext({
                name: 'Login',
                params: {
                    ...extractRequiredParams
                }
            });
        } else {
            toNext({
                name: 'Login',
                params: {
                    ...extractRequiredParams
                },
                query: {
                    redirectTo: to.fullPath
                }
            });
        }
        return;
    }
    
    // 메뉴간의 이동이 발생하여 권한 조회에 대한 transition이 필요할 때
    if (from.meta.depth1 !== to.meta.depth1) {
        store.commit('mainContentLoading');
    }

    // MFDS만 Coding 메뉴 접근 가능
    if ((to.meta.depth1 === 'Coding' && to.params.appendix !== 'KR')) {
        handleForbidden();
    }

    const getAuth = isAdmin ? SystemAdminApi.adminAuthentication() : UserAuthenticationApi.getUserAuth(sponsorKey);
    getAuth.then((res: UserAuthResponse) => {
        if (!res.success) {
            return Promise.reject(res);
        }

        commitToStore(res);
        if (isAdmin || CommonUtils.hasPrivilege(res.data.privList, to.meta.privId) || (to.meta.depth1 === 'Coding')) {
            toNext();
        } else {
            handleForbidden();
        }
    })
        .catch((res) => {
            if (typeof res === 'string') {
                MessageUtils.displayErrorMessage(res);
            } else {
                MessageUtils.displayErrorMessage(res.errMsg);
            }

            setTimeout(() => {
                const extractRequiredParams = ApiUtils.extractRequiredParams(to.params);
                if (res?.errCode === 'EXPIRED') {
                    toNext({
                        name: 'Login',
                        params: {
                            ...extractRequiredParams
                        }
                    });
                }
            }, 1000);
        });
    
    function handleForbidden() {
        const redirectTo: string = to.meta.forbiddenRedirect;
        next({
            name: redirectTo ? redirectTo : 'Forbidden',
            params: {
                ...ApiUtils.extractRequiredParams(to.params),
            }
        });
    }
    
    function commitToStore(res: UserAuthResponse) {
        AppSession.setAccessToken(res.data.token);
        store.commit('setUserId', res.data.userId);
        store.commit('setUserName', res.data.userName);
        store.commit('setRoleName', res.data.roleName);
        store.commit('setClientType', res.data.clientType);
        store.commit('setOrganization', res.data.organization);
        store.commit('setUnblind', res.data.unblind);
        store.commit('setSenderName', res.data.senderName);
        store.commit('setMenus', CommonModelUtils.getOrDefault(res.data.menuList, []));
        store.commit('setPrivileges', CommonModelUtils.getOrDefault(res.data.privList, []));
        store.commit('setEnvironment', res.data.environment);
        store.commit('setCaseNo', res.data.caseNo);
        store.commit('useWorkflow', res.data.useWorkflow);
        store.commit('setTimeZone', res.data.timeZone);

        trackerUserLogin();
    }
});

router.afterEach((to, from) => {
    store.commit('extendSessionTime');
    if (from.params.language === to.params.language && from.params.appendix === to.params.appendix) {
        store.commit('initChangeMap');
    }
    sessionStorage.removeItem('retryCount');
    store.commit('mainContentLoadingEnd');
});

router.onError((error) => {
    const pattern = /Loading chunk (.*)+ failed/g;
    const isChunkLoadFailed = error.message.match(pattern);
    if (isChunkLoadFailed) {
        refresh();
    }
});

const refresh = () => {
    let count = +(sessionStorage.getItem('retryCount') ?? 0);
    const maxTryCount = 5;
    if (count < maxTryCount) {
        sessionStorage.setItem('retryCount', (++count).toString());
        window.location.reload();
    } else {
        console.error(`max retry[${maxTryCount}] count exceed`);
    }
};

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location: any) {
    const called: any = originalPush.call(this, location);
    return called.catch((err: any) => err);
};

export default router;
